/** Remote picker widgets, bound per record kind to its own search endpoints:
 *
 *  - `createRecordPicker` — one record by id (a tour, a role, a user). Options and the
 *    picked value show name, pk and tenant (the remote-autocomplete rule).
 *  - `createTagsWidget` — a tag set: the stored tags merged with the draft's as grouped
 *    chips (kept / added / removed), and a category-grouped "Add a tag" search.
 *
 *  Picks are remembered by name in the editor (`rememberOptions`), so the ledger and the
 *  "was …" caption name them. Create a widget once (module scope or a memo) — a new
 *  component per render would remount the picker.
 */
import { Box } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import type { RecordMeta } from '@/components/RemoteAutocomplete/recordOption';

import type { RecordKey } from '../../draft/changeSet';
import { useRecordEditor } from '../../editor/RecordEditorProvider';
import type { RecordField, RecordOptionRef, RecordWidget, RecordWidgetProps } from '../../editor/types';

import { TagChips, type TagChipItem } from './TagChips';

export type RecordOptionLoader = (
  field: RecordField,
  rec: RecordKey,
  query: string,
  signal: AbortSignal,
) => Promise<RecordOptionRef[]>;

export function refMeta(r: RecordOptionRef): RecordMeta {
  return {
    name: r.label,
    pk: r.id,
    ...(r.tenant !== undefined ? { tenant: r.tenant } : {}),
    ...(r.extra ? { extra: r.extra } : {}),
  };
}

/** `#12 · GOLDEN · extra` — a ref's identity line. */
export function refTitle(r: RecordOptionRef): string {
  const bits = [`#${r.id}`];
  if (r.tenant !== undefined) bits.push(r.tenant ?? 'global');
  for (const x of r.extra ?? []) if (x) bits.push(x);
  return bits.join(' · ');
}

const refKey = (r: RecordOptionRef) => r.id;
const refLabel = (r: RecordOptionRef) => r.label;

export function createRecordPicker({
  load,
  placeholder,
  fetchOnEmpty = false,
}: {
  load: RecordOptionLoader;
  /** Shown while empty — e.g. "Guest (no staff role)" for a role picker. */
  placeholder?: string;
  /** Browsable before typing (a tenant's roles). */
  fetchOnEmpty?: boolean;
}): RecordWidget {
  function RecordPickerWidget(p: RecordWidgetProps) {
    const { optionRef, rememberOptions } = useRecordEditor();
    const { field, rec } = p;
    const queryFn = useCallback(
      (q: string, signal: AbortSignal) => load(field, rec, q, signal),
      [field, rec],
    );
    const value =
      p.value === null || p.value === undefined || p.value === ''
        ? null
        : (optionRef(field.key, p.value) ?? { id: p.value as number | string, label: `#${String(p.value)}` });
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <RemoteAutocomplete<RecordOptionRef>
          id={p.id}
          size="small"
          value={value}
          queryFn={queryFn}
          getOptionKey={refKey}
          getOptionLabel={refLabel}
          getOptionMeta={refMeta}
          fetchOnEmpty={fetchOnEmpty}
          disabled={p.disabled}
          error={p.error}
          {...(placeholder ? { placeholder } : {})}
          inputProps={{ 'aria-describedby': p.describedBy }}
          onChange={(v) => {
            const r = (v as RecordOptionRef | null) ?? null;
            if (r) rememberOptions(field.key, [r]);
            p.onChange(r ? r.id : null);
          }}
        />
      </Box>
    );
  }
  return RecordPickerWidget;
}

function asIds(value: unknown): Array<number | string> {
  return Array.isArray(value) ? (value as Array<number | string>) : [];
}

/** The wire order of a tag set: ascending ids. */
export function sortIds(ids: Array<number | string>): Array<number | string> {
  return [...new Set(ids)].sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b)),
  );
}

/** Chips for a stored set (view mode) or the stored set merged with the draft's. */
export function tagChipItems(
  field: RecordField,
  baseline: unknown,
  value: unknown,
  optionRef: (fieldKey: string, id: unknown) => RecordOptionRef | undefined,
  { merged }: { merged: boolean },
): TagChipItem[] {
  const base = asIds(baseline);
  const now = asIds(value);
  const ids = merged ? [...base, ...now.filter((id) => !base.includes(id))] : base;
  return ids.map((id) => {
    const ref = optionRef(field.key, id);
    const state = !merged ? 'kept' : !base.includes(id) ? 'added' : !now.includes(id) ? 'removed' : 'kept';
    return {
      id,
      label: ref?.label ?? `#${String(id)}`,
      group: ref?.group ?? 'Other',
      state,
      ...(ref ? { title: refTitle(ref) } : {}),
    };
  });
}

/** The "Add a tag" search's height (a small outlined input). */
export const TAGS_ADD_ROW_PX = 40;

export function createTagsWidget({
  load,
  placeholder = 'Add a tag',
  reserveAddRow = false,
}: {
  load: RecordOptionLoader;
  /** The search input's placeholder — or one per field (a tour's packages: "Add a
   *  package"). */
  placeholder?: string | ((field: RecordField) => string);
  /** The read-only chips keep the search's row under them, so pressing E moves nothing
   *  (the catalog records' Zero-Re-Layout). */
  reserveAddRow?: boolean;
}): RecordWidget {
  function TagsWidget(p: RecordWidgetProps) {
    const { optionRef, rememberOptions } = useRecordEditor();
    const { field, rec, onChange } = p;
    const queryFn = useCallback(
      (q: string, signal: AbortSignal) => load(field, rec, q, signal),
      [field, rec],
    );
    const now = asIds(p.value);
    const selected = useMemo(
      () => now.map((id) => optionRef(field.key, id) ?? { id, label: `#${String(id)}` }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [JSON.stringify(now), field.key, optionRef],
    );
    const items = tagChipItems(field, p.baseline, p.value, optionRef, { merged: true });
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <TagChips
          items={items}
          label={field.label}
          onRemove={(id) => onChange(sortIds(now.filter((x) => x !== id)))}
          onRestore={(id) => onChange(sortIds([...now, id]))}
        />
        <RemoteAutocomplete<RecordOptionRef>
          id={p.id}
          size="small"
          multiple
          value={selected}
          renderTags={() => null}
          queryFn={queryFn}
          getOptionKey={refKey}
          getOptionLabel={refLabel}
          getOptionMeta={refMeta}
          groupBy={(r) => r.group ?? 'Other'}
          fetchOnEmpty
          placeholder={typeof placeholder === 'function' ? placeholder(field) : placeholder}
          disabled={p.disabled}
          error={p.error}
          inputProps={{ 'aria-describedby': p.describedBy }}
          onChange={(v) => {
            const list = (v as RecordOptionRef[] | null) ?? [];
            rememberOptions(field.key, list);
            onChange(sortIds(list.map((r) => r.id)));
          }}
        />
      </Box>
    );
  }
  if (reserveAddRow) (TagsWidget as RecordWidget).reserveBelow = TAGS_ADD_ROW_PX;
  return TagsWidget;
}
